import classNames from 'classnames';
import { Base64 } from 'js-base64';
import { useEffect, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useRouteMatch } from 'react-router';

import { DownloadIcon } from '../../../../assets/icons';
import { TermsAndConditionsIllustration } from '../../../../assets/illustrations';
import Button from '../../../../components/atoms/Button';
import { HtmlContent } from '../../../../components/atoms/RenderContent';
import { TITLE_SIZE, TITLE_TAG } from '../../../../components/atoms/Title';
import Card from '../../../../components/molecules/Card/Card';
import Column from '../../../../components/organisms/Column';
import Container from '../../../../components/organisms/Container';
import LoadingPage from '../../../../components/templates/LoadingPage/LoadingPage';
import SimpleFormPage from '../../../../components/templates/SimpleFormPage/SimpleFormPage';
import { SIZE, LOCAL_STORAGE_KEYS } from '../../../../constants';
import useGeoCode from '../../../../helpers/hooks/useGeoCode';
import { saveHtmlAsPdf } from '../../../../helpers/misc';
import { State } from '../../../../types/state.types';
import { setGeography } from '../../actions';
import { useGetPublicLegalDocumentQuery } from '../../api/legalDocuments/legalDocumentsApi';
import { legalDocumentsConfig, legalDocType } from '../../config';
import { getLegalDocLabel } from '../../helpers/helpers';
import { useCoreTranslation } from '../../hooks/useCoreTranslation';
import GeographySelector from '../GeographySelector';

import { publicLegalDocTypeNamesToCodeMap } from './PublicLegalDocPage.types';

import { useLazyGetGeographiesQuery } from '@/modules/Core/api/geolocations/geolocationsApi';

import styles from './PublicLegalDocPage.module.css';

const PublicLegalDocPage = () => {
  const {
    params: { document_type: type },
  } = useRouteMatch<any>();

  const { label } = useCoreTranslation(__filename);

  const {
    geographies: { list: geographies },
  } = useSelector((state: State) => state.Shared);
  const dispatch = useDispatch();
  const [getGeographies] = useLazyGetGeographiesQuery();
  const [hasValidGeography, setHasValidGeography] = useState<boolean | null>(null);

  const documentId = publicLegalDocTypeNamesToCodeMap[type].toString() || '';
  const { shouldDisplayDownloadButton } = legalDocumentsConfig();

  const { currentGeoCode, defaultGeoCode } = useGeoCode();
  const currentRegionCode = localStorage.getItem(LOCAL_STORAGE_KEYS.CURRENT_REGION_CODE);

  const showGeographies =
    type === legalDocType.privacy_policy && (!currentGeoCode || !currentRegionCode);

  const { data: legalDoc, isFetching: legalDocLoading } = useGetPublicLegalDocumentQuery(
    { id: documentId, geoCode: currentGeoCode },
    { skip: showGeographies }
  );

  useEffect(
    function loadGeographies() {
      if (!geographies || geographies?.length === 0) {
        getGeographies();
      }
    },
    [geographies, getGeographies]
  );

  useEffect(
    function setCurrentGeography() {
      if (!geographies || geographies.length < 1) {
        return;
      }

      const isInList = !!geographies.find(({ code }) => code === currentGeoCode);
      if (isInList) {
        localStorage.setItem(LOCAL_STORAGE_KEYS.CURRENT_GEO_CODE, currentGeoCode);
      }
      setHasValidGeography(isInList);
    },
    [currentGeoCode, geographies]
  );

  useEffect(() => {
    if (!showGeographies) {
      if (defaultGeoCode) {
        localStorage.setItem(LOCAL_STORAGE_KEYS.CURRENT_GEO_CODE, defaultGeoCode);
      }
      // if ion-modal is opened on page load without user interaction. it creates 2 modals ad screen has a backdrop
      // force remove them with JS
      document.querySelectorAll('#geography_modal').forEach((modalElement) => {
        modalElement.remove();
      });
    }
  }, [documentId, currentGeoCode, dispatch, showGeographies, defaultGeoCode]);

  const handleGeographyChange = useCallback(
    (code: string) => {
      if (code) {
        localStorage.setItem(LOCAL_STORAGE_KEYS.CURRENT_GEO_CODE, code);
        dispatch(setGeography({ geoCode: code }));
      }
    },
    [dispatch]
  );

  if (hasValidGeography === null || legalDocLoading || !legalDoc) {
    return <LoadingPage />;
  }

  return (
    <SimpleFormPage
      withNavBar={false}
      hasBackLink={false}
      hideAllWidgets={true}
      title={getLegalDocLabel(type, label)}
    >
      <Container>
        <Column.Main>
          {showGeographies ? (
            <div className={classNames(styles.geographiesWrapper)}>
              <Card className={classNames(styles.card)}>
                <span>{label('Ref: To view Privacy Policy please select your country.')}</span>
              </Card>
              <Card className={classNames(styles.card)}>
                <GeographySelector
                  geographies={geographies}
                  handleGeographySubmit={handleGeographyChange}
                  defaultOpenModal={true}
                />
              </Card>
            </div>
          ) : (
            <Card
              overTitle={{
                tag: TITLE_TAG.H1,
                size: TITLE_SIZE.HEADLINES,
                children: getLegalDocLabel(type, label),
                className: styles.legalTitle,
                ...(shouldDisplayDownloadButton
                  ? {
                      suffix: (
                        <Button
                          look="tertiary"
                          onClick={() => saveHtmlAsPdf(Base64.decode(legalDoc.data))}
                          size={SIZE.SMALL}
                          inheritStyle={styles.titleSuffix}
                          data-testid="legal-docs-download"
                        >
                          <DownloadIcon className={classNames(styles.titleIcon)} />
                        </Button>
                      ),
                    }
                  : {}),
              }}
            >
              <div
                id="legalDocumentContainer"
                data-cy="legal-document-container"
                className={classNames(styles.wrapper)}
              >
                <HtmlContent data-testid="public-legal-doc-page-html-content">
                  {Base64.decode(legalDoc.data)}
                </HtmlContent>
              </div>
            </Card>
          )}
        </Column.Main>
        <Column.Complementary>
          <TermsAndConditionsIllustration />
        </Column.Complementary>
      </Container>
    </SimpleFormPage>
  );
};

export default PublicLegalDocPage;
